import { ChevronDown, Languages } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { TARGET_LABELS, TRANSLATION_TARGETS, type TranslationTarget } from '@/lib/translation'
import { cn } from '@/lib/utils'
import { useTranslationSettings, type TranslationSettings } from './TranslationProvider'

function LanguageChoice({ settings }: { settings: TranslationSettings }) {
  return (
    <>
      <DropdownMenuLabel className="text-xs text-muted-foreground">
        Translate into
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={settings.target}
        onValueChange={(value) => settings.setTarget(value as TranslationTarget)}
      >
        {TRANSLATION_TARGETS.map((language) => (
          <DropdownMenuRadioItem key={language} value={language}>
            {TARGET_LABELS[language]}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}

/**
 * "Translate page" in the header: every translatable block on the page shows
 * its translation, and stays so from page to page until switched off. Beside
 * it, the language to translate into. Both are remembered for the person. On
 * a phone the header has no room for two controls, so both are in one menu.
 */
export function TranslatePageMenu() {
  const settings = useTranslationSettings()
  if (!settings) return null
  const { pageOn, setPageOn, target } = settings
  return (
    <>
      <div
        className={cn(
          'hidden items-center rounded-full border border-transparent sm:flex',
          pageOn && 'border-border bg-muted',
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={pageOn}
          aria-label="Translate page"
          title={
            pageOn
              ? 'Show this page as written'
              : `Translate this page into ${TARGET_LABELS[target]}`
          }
          onClick={() => setPageOn(!pageOn)}
          className="gap-1.5 rounded-full px-2.5"
        >
          <Languages className={cn('size-4', pageOn && 'text-primary')} />
          <span className="hidden lg:inline">{pageOn ? 'Translated' : 'Translate page'}</span>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Translation language: ${TARGET_LABELS[target]}`}
              className="-ml-1 gap-0.5 rounded-full px-1.5 text-xs font-semibold uppercase tabular-nums"
            >
              {target}
              <ChevronDown aria-hidden className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <LanguageChoice settings={settings} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={pageOn ? 'Translation: on' : 'Translation'}
            className={cn('rounded-full sm:hidden', pageOn && 'bg-muted')}
          >
            <Languages className={cn('size-4', pageOn && 'text-primary')} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuCheckboxItem checked={pageOn} onCheckedChange={(on) => setPageOn(on)}>
            Translate page
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <LanguageChoice settings={settings} />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
